import React from 'react';
import { IStudent, StudentAnswerProps } from '@/components/staffer/Grading/types';
import { formattedUpdatedAt } from '@/components/staffer/Grading/utils';
import TeacherCommentAndScore from '@/components/staffer/Grading/TeacherCommentAndScore';
import styles from '@/components/staffer/Grading/QuestionCards/questionCard.module.scss';
import StudentAnswerInformation from '@/components/staffer/Grading/QuestionCards/StudentAnswerInformation';
import {
  DragAndDropQuestionStudentAnswer,
  DrawingQuestionStudentAnswer,
  FillInTheBlankQuestionStudentAnswer,
  GridQuestionStudentAnswer,
  ImageMultipleChoiceQuestionStudentAnswer,
  MultipleChoiceQuestionStudentAnswer,
  SliderQuestionStudentAnswer,
  WritingQuestionStudentAnswer,
} from '@/components/staffer/Grading/StudentAnswers/index';
import useGradingStore from '@/components/staffer/Grading/useGradingStore';
import PendingStripe from '@/components/staffer/Grading/AISuggestion/PendingStripe';
import useDisplayedSuggestion from '@/components/staffer/Grading/AISuggestion/useDisplayedSuggestion';
import cn from '@/utils/cn';
import AssessmentFlashMessage from './AssessmentFlashMessage';

const COMPONENT_MAP = {
  DragAndDropQuestion: DragAndDropQuestionStudentAnswer,
  DrawingQuestion: DrawingQuestionStudentAnswer,
  FillInTheBlankQuestion: FillInTheBlankQuestionStudentAnswer,
  GridQuestion: GridQuestionStudentAnswer,
  ImageMultipleChoiceQuestion: ImageMultipleChoiceQuestionStudentAnswer,
  MultipleChoiceQuestion: MultipleChoiceQuestionStudentAnswer,
  OpenEndedQuestion: WritingQuestionStudentAnswer,
  SliderQuestion: SliderQuestionStudentAnswer,
};

interface StudentAnswerCardProps extends StudentAnswerProps {
  student: IStudent
}

export const StudentAnswerCard = ({ question, questionAnswer, student }: StudentAnswerCardProps) => {
  const Component = COMPONENT_MAP[question.type];
  if (!Component) return null;

  const { isAssessment, isTeacher, submissionForStudent } = useGradingStore();
  const submission = submissionForStudent(student.id);
  const displayedSuggestion = useDisplayedSuggestion(questionAnswer);
  const hasPendingSuggestion = isTeacher && displayedSuggestion?.state === 'ai_suggested_pending';

  const studentName = [student.first_name, student.last_name].filter(Boolean).join(' ');

  const HeaderInformation = () => {
    if (isAssessment() && submission?.submitted_at == null) {
      return (
        <AssessmentFlashMessage
          student={student}
          submission={submission}
        />
      );
    }

    if (questionAnswer) {
      return (
        <div className="tw-text-xs">
          {`Last updated on: ${formattedUpdatedAt(questionAnswer.answer_updated_at)}`}
        </div>
      );
    }

    return null;
  };

  return (
    <div className={cn(styles.card, { 'tw-relative': hasPendingSuggestion })}>
      {hasPendingSuggestion && <PendingStripe />}
      <div className={`${styles.header} tw-flex tw-justify-between tw-items-center`}>
        <div className="tw-text-base">
          {studentName}
        </div>
        <HeaderInformation />
      </div>
      <div className={styles.bodyWrapper}>
        <>
          <StudentAnswerInformation updatedAt={questionAnswer?.answer_updated_at} />
          <div className={`${styles.studentResponse} ${questionAnswer ? styles.answered : styles.unanswered}`}>
            <Component
              question={question}
              questionAnswer={questionAnswer}
            />
          </div>
          <TeacherCommentAndScore
            question={question}
            questionAnswer={questionAnswer}
            studentId={student.id}
          />
        </>
      </div>
    </div>
  );
};

export default StudentAnswerCard;
